
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Branch {
  id: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  latitude: number;
  longitude: number;
  is_active: boolean;
}

const BranchesMap = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      // Validate environment variables
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Missing Supabase configuration');
      }

      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setBranches(data || []);
      if (data && data.length > 0) {
        setSelectedBranch(data[0]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line animate-spin text-4xl text-[#E31B23] mb-4"></i>
        <p className="text-gray-600">Chargement de la carte...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
        <p className="text-gray-600">Une erreur est survenue. Veuillez réessayer plus tard.</p>
      </div>
    );
  }

  if (branches.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="ri-map-pin-line text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-600">Aucune succursale trouvée</p>
      </div>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Nos succursales en Algérie
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Nous sommes présents dans plusieurs wilayas pour mieux vous servir. Trouvez la succursale la plus proche de chez vous.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Branches List */}
          <div className="lg:col-span-1 space-y-4">
            {branches.map((branch) => (
              <div
                key={branch.id}
                onClick={() => setSelectedBranch(branch)}
                className={`bg-white rounded-xl shadow-sm border-2 p-6 transition-all cursor-pointer ${
                  selectedBranch?.id === branch.id
                    ? 'border-[#E31B23] shadow-lg'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedBranch?.id === branch.id
                      ? 'bg-[#E31B23] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <i className="ri-map-pin-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{branch.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{branch.city}</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-map-pin-line mt-0.5 flex-shrink-0"></i>
                        <span>{branch.address}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-phone-line flex-shrink-0"></i>
                        <a href={`tel:${branch.phone}`} className="hover:text-[#E31B23] transition-colors">
                          {branch.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-mail-line flex-shrink-0"></i>
                        <a href={`mailto:${branch.email}`} className="hover:text-[#E31B23] transition-colors">
                          {branch.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Map */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden h-[600px]">
              {selectedBranch && (
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${selectedBranch.latitude},${selectedBranch.longitude}&zoom=15`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Carte de ${selectedBranch.name}`}
                ></iframe>
              )}
            </div>

            {/* Selected Branch Info */}
            {selectedBranch && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedBranch.name}</h3>
                    <p className="text-gray-600 mb-4">{selectedBranch.address}, {selectedBranch.city}</p>
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedBranch.latitude},${selectedBranch.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-[#E31B23] text-white rounded-lg font-medium hover:bg-[#C41820] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-navigation-line"></i>
                    Itinéraire
                  </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <a
                    href={`tel:${selectedBranch.phone}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#E31B23] text-white rounded-lg flex items-center justify-center">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Téléphone</p>
                      <p className="font-semibold text-gray-900">{selectedBranch.phone}</p>
                    </div>
                  </a>
                  <a
                    href={`mailto:${selectedBranch.email}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#E31B23] text-white rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Email</p>
                      <p className="font-semibold text-gray-900">{selectedBranch.email}</p>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BranchesMap;
